/**
 * Plausibility check for a legal name before it is written to a client's PII.
 *
 * CPK-4199: the SomosKycV1 pre-review adopts the document's extracted identity into
 * `cp_pii.legal_name`. CPF was validated by check digit and the date of birth by `Date.parse`,
 * but the name was only checked for being non-empty — so an OCR read of a bilingual ID label
 * ("NOME / NAME", "NOME SOCIAL / SOCIAL NAME") was stored verbatim as the client's legal name.
 * 170 clients in production ended up as "/ NAME" and were then failed by Veriff/Serpro for a
 * name mismatch we had created ourselves.
 *
 * This is the last gate on the client side; the `/profile` lambda enforces the same rule so no
 * future caller can bypass it.
 */

/** Field-label vocabulary (pt + en) — a real name NEVER contains any of these as a token. */
const LABEL_WORDS = new Set([
  'NOME', 'NOMES', 'NAME', 'NAMES', 'SOCIAL', 'SOBRENOME', 'SURNAME', 'GIVEN', 'COMPLETO',
  'FULL', 'CIVIL', 'FILIACAO', 'PARENTS', 'PARENT', 'MAE', 'PAI', 'MOTHER', 'FATHER',
  'TITULAR', 'PORTADOR', 'HOLDER', 'ASSINATURA', 'SIGNATURE', 'DOCUMENTO', 'DOCUMENT',
]);

/** A single name token: letters only, with internal apostrophes/hyphens (D'Ávila, Ana-Maria). */
const NAME_TOKEN = /^[A-Za-zÀ-ÿ]+(?:['’-][A-Za-zÀ-ÿ]+)*$/;

/** Accent-folded uppercase, for matching against the label vocabulary. */
function foldUpper(s: string): string {
  return s
    .toUpperCase()
    .replace(/[ÁÀÂÃÄ]/g, 'A')
    .replace(/[ÉÈÊË]/g, 'E')
    .replace(/[ÍÌÎÏ]/g, 'I')
    .replace(/[ÓÒÔÕÖ]/g, 'O')
    .replace(/[ÚÙÛÜ]/g, 'U')
    .replace(/Ç/g, 'C')
    .replace(/Ñ/g, 'N');
}

/**
 * True when `value` can plausibly be a person's legal name.
 *
 * Rejects: empty/whitespace, field separators (`/ | \`), any digit, a single token, tokens with
 * stray punctuation, single-letter scraps, and any document-label word among the tokens.
 * Deliberately permissive otherwise — a guard against structural garbage, not a spell-checker.
 *
 * CPK-4341: OCR garbles of the bilingual label line carry no slash and mix a label word with
 * scraps ("! Name S", "Name, Te", "(soc Name e"), so the CPK-4199 rules (slash + all-label-words)
 * let them through. A name token must be letters only (internal '/- allowed), single letters are
 * OCR scraps (except the connector "E"), and ANY label-word token disqualifies the value.
 */
export function isPlausibleLegalName(value: string | null | undefined): boolean {
  // CPK-5328: NFD accents ("a" + U+0301) are real names — compose before the token check.
  const name = value?.normalize('NFC').trim();
  if (!name || name.length < 3 || name.length > 120) return false;
  // Field separators mean we captured a label or a composite field, not a person.
  if (/[/|\\]/.test(name)) return false;
  // Real names carry no digits.
  if (/\d/.test(name)) return false;

  const tokens = name.split(/\s+/);
  if (tokens.length < 2) return false;
  if (!tokens.every((t) => NAME_TOKEN.test(t))) return false;
  const folded = tokens.map((t) => foldUpper(t));
  if (folded.some((t) => t.length === 1 && t !== 'E')) return false;
  // "NOMES" is exempt from the any-token rule: it exists as a real (rare) Brazilian surname, and
  // no observed corruption depends on it alone — the garbles always carry NOME/NAME/SOCIAL.
  if (folded.some((t) => LABEL_WORDS.has(t) && t !== 'NOMES')) return false;

  return true;
}

/**
 * CPK-5328: check for ONE name field typed by the client (questionnaire first name, surname or full
 * name). On 2026-10-28 a client replaced the prefilled name with "<x>" + "Portador" and signed the
 * contract with it — the question regex only blocks digits and symbols.
 *
 * Narrower than isPlausibleLegalName on purpose: a single token and a stray initial are NOT rejected
 * here, because the field is prefilled with the stored name and ~400 stored names carry an initial
 * ("Maria S Silva") — rejecting them would trap those clients on the page. The backend guard still
 * validates the combined name before it is written.
 */
export function isPlausibleNamePart(value: string | null | undefined): boolean {
  const name = value?.normalize('NFC').trim();
  if (!name) return false;

  const tokens = name.split(/\s+/);
  if (!tokens.every((t) => NAME_TOKEN.test(t))) return false;
  return !tokens.map((t) => foldUpper(t)).some((t) => LABEL_WORDS.has(t) && t !== 'NOMES');
}
